// The frontend Docker image builds from the `frontend/` context only, so the
// detail drawer ships its own AppSheet/Trade VMT mapping copy instead of
// importing the repo-level `shared/` module at bundle time. Keep this file
// aligned with `shared/appsheetVmtMapping.js`; parity tests exist to catch drift.
const VMT_FIELD_MAP = [
  { field: 'selectionDate', appsheet: ['selectionDate', 'SelectionDate', 'productSelectionDate', 'Product Selection Date'], required: true },
  { field: 'effectiveDate', appsheet: ['effectiveDate', 'EffectiveDate'], required: true },
  { field: 'bac', appsheet: ['Dealer BAC', 'BAC'], required: true },
  { field: 'countryCd', appsheet: ['countryCd', 'CountryCd', 'countryCode', 'Country Code'], required: true },
  { field: 'categoryCd', appsheet: ['categoryCd', 'CategoryCd'], required: true },
  {
    field: 'vendorCd',
    appsheet: ['venderCd', 'vendorCd', 'VenderCd', 'VendorCd', 'popcornproductcodevendercode', 'popcornproductcodevendorcode'],
    required: true,
  },
  {
    field: 'gmBrandCd',
    appsheet: ['gmBrandCd', 'GM Brand Code', 'brandCd', 'popcornproductcodebrand'],
    required: true,
  },
  {
    field: 'productCd',
    appsheet: ['OemProductCodePopcorn', 'OEM Product Code', 'ProductCode__c', 'productCd', 'Product Code', 'popcornproductcode'],
    required: true,
  },
  {
    field: 'productDescription',
    appsheet: ['productDescription', 'Product Description', 'serviceItemName', 'Service Item', 'Product_Name__c', 'Fixed ProductName__c'],
    required: true,
  },
  { field: 'designation', appsheet: ['designation', 'Designation', 'type', 'Type'], required: true },
  { field: 'itemQuantity', appsheet: ['itemQuantity', 'ItemQuantity', 'quantity', 'Quantity'], required: true },
  { field: 'status', appsheet: ['Status', 'status', 'Effective Status', 'effective status', 'VmtCallStatus'], required: true },
  { field: 'lastUpdatedDate', appsheet: ['lastUpdatedDate', 'LastUpdatedDate', 'Last Updated Date'], required: true },
  { field: 'lastUpdatedBy', appsheet: ['lastUpdatedBy', 'LastUpdatedBy', 'Last Updated By'], required: true },
  { field: 'billingDate', appsheet: ['billingDate', 'BillingDate', 'billingStartDate', 'Billing Start Date'], required: false },
  { field: 'serviceMonth', appsheet: ['serviceMonth', 'ServiceMonth'], required: false },
  { field: 'vendorInvoiceNumber', appsheet: ['Vender Invoice Number', 'vendorInvoiceNumber'], required: false },
  { field: 'occurrence', appsheet: ['Occurance', 'Occurrence', 'occurrence'], required: false },
  { field: 'dealerPrice', appsheet: ['dealerPrice', 'Dealer Price'], required: false },
  { field: 'filler', appsheet: ['filler', 'Filler'], required: false },
  { field: 'promoCd', appsheet: ['promoCd', 'PromoCd'], required: false },
  { field: 'imrUsage', appsheet: ['imrUsage', 'ImrUsage'], required: false },
  { field: 'imrPercentage', appsheet: ['imrPercentage', 'ImrPercentage'], required: false },
  { field: 'referenceId', appsheet: ['id', 'Id', 'referenceId', 'ReferenceId'], required: false },
  { field: 'futureUse1', appsheet: ['futureUse1', 'FutureUse1'], required: false },
  { field: 'futureUse2', appsheet: ['futureUse2', 'FutureUse2'], required: false },
  { field: 'futureUse3', appsheet: ['futureUse3', 'FutureUse3'], required: false },
];

const REQUIRED_FIELDS = VMT_FIELD_MAP.filter((entry) => entry.required).map((entry) => entry.field);
const ORDER_ITEM_ACCOUNT_ID_FIELDS = ['Account_ID__c', 'account_id__c', 'AccountId', 'accountId', 'account_id', 'Account ID'];
const ORDER_ITEM_ROW_ID_FIELDS = ['__rowId', 'Id', 'id', 'ID'];
const APPSHEET_VMT_IMPLICIT_DEFAULTS = Object.freeze({
  countryCd: 'US', vendorCd: '5D', itemQuantity: '1', occurrence: 'Monthly',
  imrUsage: 'Y', imrPercentage: '100', lastUpdatedBy: 'Unknown',
});
const TRADE_VMT_RECORD_DEFAULTS = Object.freeze({ designation: 'Package' });
const TRADE_VMT_RECORD_OVERRIDES = Object.freeze({ programName: 'Trade', categoryCd: 'TRTL', vendorCd: '84' });

const getCaseInsensitiveValue = (row, field) => {
  if (!row || !field) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, field)) return row[field];
  const normalized = String(field).toLowerCase();
  const match = Object.keys(row).find((key) => String(key).toLowerCase() === normalized);
  return match ? row[match] : undefined;
};

const sanitizeValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value);
};

const hasConfiguredValue = (config = {}, field) => {
  if (!config || !Object.prototype.hasOwnProperty.call(config, field)) return false;
  return sanitizeValue(config[field]) !== '';
};

const getRequiredSourceFieldGroups = ({ defaults = {}, overrides = {}, extraRequiredGroups = [] } = {}) => {
  const resolvedDefaults = { ...APPSHEET_VMT_IMPLICIT_DEFAULTS, ...(defaults || {}) };
  const requiredGroups = VMT_FIELD_MAP
    .filter(({ required, field }) => required && !hasConfiguredValue(resolvedDefaults, field) && !hasConfiguredValue(overrides, field))
    .map(({ appsheet }) => (Array.isArray(appsheet) ? [...appsheet] : [appsheet]));
  const extraGroups = (extraRequiredGroups || []).map((group) => (Array.isArray(group) ? [...group] : [group]));
  return [...requiredGroups, ...extraGroups];
};

const getMappedValue = (row, appsheetField) => {
  const candidates = Array.isArray(appsheetField) ? appsheetField : [appsheetField];
  for (const candidate of candidates) {
    const value = getCaseInsensitiveValue(row, candidate);
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return undefined;
};

const getTodayVmtDate = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const year = String(now.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const normalizeOutboundBrand = (value) => {
  const brand = sanitizeValue(value).toUpperCase();
  return brand === 'K' ? 'CAD' : brand;
};

const isTradeRow = (row, overrides) => {
  const program = sanitizeValue(
    overrides?.programName || getMappedValue(row, ['programName', 'Product Program', 'productProgram', 'Program'])
  ).toLowerCase();
  return program === 'trade';
};

const buildAppsheetVmtRecord = (row = {}, options = {}) => {
  const { stampLastUpdatedDate = false, defaults = {}, overrides = {} } = options;
  const record = {};
  VMT_FIELD_MAP.forEach(({ field, appsheet }) => {
    record[field] = sanitizeValue(getMappedValue(row, appsheet));
  });
  Object.entries(defaults || {}).forEach(([field, value]) => {
    if (!record[field] || !String(record[field]).trim()) {
      record[field] = sanitizeValue(value);
    }
  });
  Object.entries(overrides || {}).forEach(([field, value]) => {
    record[field] = sanitizeValue(value);
  });
  record.countryCd = 'US';
  record.vendorCd = isTradeRow(row, overrides) ? '84' : '5D';
  record.vendorInvoiceNumber = record.bac;
  record.gmBrandCd = normalizeOutboundBrand(record.gmBrandCd);
  record.itemQuantity = '1';
  record.occurrence = 'Monthly';
  record.promoCd = null;
  record.imrUsage = 'Y';
  record.imrPercentage = '100';
  record.lastUpdatedBy = 'Unknown';
  record.futureUse1 = null;
  record.futureUse2 = null;
  record.futureUse3 = null;
  if (stampLastUpdatedDate) {
    record.lastUpdatedDate = getTodayVmtDate();
  }
  return record;
};

const getMissingRequiredFields = (row = {}, options = {}) => {
  const record = buildAppsheetVmtRecord(row, options);
  const missing = REQUIRED_FIELDS.filter((field) => !record[field] || !String(record[field]).trim());
  const allowedBrands = new Set(['C', 'G', 'B', 'CAD', 'CB', 'CG', 'BG', 'CBG']);
  if (record.gmBrandCd && !allowedBrands.has(record.gmBrandCd) && !missing.includes('gmBrandCd')) missing.push('gmBrandCd');
  return missing;
};

export {
  VMT_FIELD_MAP,
  REQUIRED_FIELDS,
  ORDER_ITEM_ACCOUNT_ID_FIELDS,
  ORDER_ITEM_ROW_ID_FIELDS,
  APPSHEET_VMT_IMPLICIT_DEFAULTS,
  TRADE_VMT_RECORD_DEFAULTS,
  TRADE_VMT_RECORD_OVERRIDES,
  getCaseInsensitiveValue,
  sanitizeValue,
  getRequiredSourceFieldGroups,
  getTodayVmtDate,
  buildAppsheetVmtRecord,
  getMissingRequiredFields,
};
